import axios, { AxiosResponse } from "axios";
import { useMemo } from "react";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import {
    MktApiKey, MktApiKeyCreated, MktAttributionGroup, MktAttributionModel, MktAttributionReport, MktAuditEntry, MktCampaign, MktLinkInput, MktLinksView, MktTrackedLink, MktCampaignReport, MktCampaignStats, MktContactDetail, MktContactRow, MktEventDef, MktFieldDef,
    MktMergeField, MktOverview, MktPreferencePage, MktPreflight, MktRendered, MktRuleCheck, MktSegment, MktSegmentDefinition,
    MktOAuthRequestView, MktProposalStatus, MktProposalView, MktScope, MktSegmentPreview, MktSend, MktSignal, MktSignalDay, MktSignalInput, MktSignalsView,
    MktTemplate, MktTemplateDoc, MktTopic, MktWorkspaceView
} from "./types";
import {
    MktFlow, MktFlowCheck, MktFlowDetail, MktFlowDraft, MktFlowListItem, MktFlowLog, MktFlowPerson, MktFlowReport, MktFlowSettings, MktFlowStarter, MktFlowTrigger
} from "./flow_types";

import { MktAnalyticsOverview, MktAnalyticsQuery, MktAnalyticsRange, MktFlowAnalytics, MktFlowSummary } from "./analytics_types";

export * from "./types";
export * from "./flow_types";
export * from "./analytics_types";

const baseUrl = `${environment.v2Url}/marketing`;
const publicUrl = `${environment.v2Url}/public/marketing`;

/** Every call resolves to its data or to this; nothing throws. */
export interface MktError { ok: false; status: number; message: string }
export const isMktError = (v: unknown): v is MktError =>
    !!v && typeof v === 'object' && (v as MktError).ok === false && typeof (v as MktError).message === 'string';

export interface MktPage<T> { rows: T[]; total: number }

export interface MktContactQuery {
    q?: string;
    /** subscribed | unsubscribed | suppressed | no_basis */
    status?: string;
    topic_id?: number;
    segment_id?: number;
    page?: number;
    size?: number;
}

export interface MktCatalog { fields: MktFieldDef[]; events: MktEventDef[]; merge_fields: MktMergeField[] }

export interface MktWorkspaceInput {
    from_name?: string;
    from_email?: string | null;
    reply_to?: string | null;
    postal_address?: string | null;
    sending_domain?: string | null;
    brand_kit?: MktWorkspaceView['workspace']['brand_kit'];
    settings?: Partial<MktWorkspaceView['settings']>;
}

export interface MktTemplateInput { name?: string; subject?: string; preheader?: string; doc?: MktTemplateDoc }

export interface MktCampaignInput {
    name?: string;
    segment_id?: number | null;
    topic_id?: number | null;
    template_id?: number | null;
    subject?: string;
    preheader?: string;
    from_name?: string | null;
    reply_to?: string | null;
    scheduled_at?: string | null;
    holdout_pct?: number;
}

/** Partial: only what is sent changes. Send the whole draft when steps or the goal change. */
export interface MktFlowInput {
    name?: string;
    description?: string | null;
    trigger?: MktFlowTrigger | null;
    topic_id?: number | null;
    draft?: MktFlowDraft;
    settings?: MktFlowSettings;
}

export type MktFlowPeopleStatus = 'active' | 'completed' | 'exited' | 'cancelled' | 'failed';

export interface MktImportRow { email: string; first_name?: string; last_name?: string; company?: string; country?: string }
export interface MktImportResult { created: number; updated: number; skipped: { row: number; email: string; reason: string }[] }

export interface MktApiKeyInput { name: string; scopes: MktScope[]; expires_in_days: number }

export type MarketingKeyFunctions = {
    // Workspace
    workspace: () => Promise<MktWorkspaceView | MktError>;
    saveWorkspace: (input: MktWorkspaceInput) => Promise<MktWorkspaceView | MktError>;
    overview: () => Promise<MktOverview | MktError>;
    catalog: () => Promise<MktCatalog | MktError>;
    saveTopic: (topic: Partial<MktTopic> & { name: string }) => Promise<MktTopic | MktError>;
    audit: () => Promise<MktAuditEntry[] | MktError>;
    // Contacts
    contacts: (query: MktContactQuery) => Promise<MktPage<MktContactRow> | MktError>;
    contact: (id: number) => Promise<MktContactDetail | MktError>;
    setConsent: (id: number, topicId: number, status: 'subscribed' | 'unsubscribed', note?: string) => Promise<MktContactDetail | MktError>;
    suppress: (id: number, note?: string) => Promise<MktContactDetail | MktError>;
    unsuppress: (id: number, note: string) => Promise<MktContactDetail | MktError>;
    erase: (id: number) => Promise<{ erased: true } | MktError>;
    exportContact: (id: number) => Promise<Record<string, unknown> | MktError>;
    importContacts: (rows: MktImportRow[], topicKeys: string[], basis: 'consent' | 'imported', wording: string) => Promise<MktImportResult | MktError>;
    syncContacts: () => Promise<{ contacts: number; created: number; updated: number } | MktError>;
    // Segments
    segments: () => Promise<MktSegment[] | MktError>;
    segment: (id: number) => Promise<MktSegment | MktError>;
    createSegment: (input: { name: string; description?: string | null; definition: MktSegmentDefinition }) => Promise<MktSegment | MktError>;
    updateSegment: (id: number, input: { name?: string; description?: string | null; definition?: MktSegmentDefinition }) => Promise<MktSegment | MktError>;
    archiveSegment: (id: number) => Promise<true | MktError>;
    previewSegment: (definition: MktSegmentDefinition) => Promise<MktSegmentPreview | MktError>;
    whyInSegment: (id: number, contactId: number) => Promise<{ in: boolean; checks: MktRuleCheck[] } | MktError>;
    // Templates
    templates: () => Promise<MktTemplate[] | MktError>;
    template: (id: number) => Promise<MktTemplate | MktError>;
    createTemplate: (input: MktTemplateInput & { name: string }) => Promise<MktTemplate | MktError>;
    updateTemplate: (id: number, input: MktTemplateInput) => Promise<MktTemplate | MktError>;
    duplicateTemplate: (id: number) => Promise<MktTemplate | MktError>;
    archiveTemplate: (id: number) => Promise<true | MktError>;
    render: (input: { subject: string; preheader: string; doc: MktTemplateDoc; contact_id?: number | null }) => Promise<MktRendered | MktError>;
    sendTest: (input: { subject: string; preheader: string; doc: MktTemplateDoc }) => Promise<{ sent_to: string } | MktError>;
    // Campaigns
    campaigns: () => Promise<(MktCampaign & { stats: MktCampaignStats | null })[] | MktError>;
    campaign: (id: number) => Promise<MktCampaign | MktError>;
    createCampaign: (input: MktCampaignInput & { name: string }) => Promise<MktCampaign | MktError>;
    updateCampaign: (id: number, input: MktCampaignInput) => Promise<MktCampaign | MktError>;
    duplicateCampaign: (id: number) => Promise<MktCampaign | MktError>;
    preflight: (id: number) => Promise<MktPreflight | MktError>;
    submit: (id: number, note?: string) => Promise<MktCampaign | MktError>;
    approve: (id: number) => Promise<MktCampaign | MktError>;
    reject: (id: number, note: string) => Promise<MktCampaign | MktError>;
    cancel: (id: number) => Promise<MktCampaign | MktError>;
    pause: (id: number) => Promise<MktCampaign | MktError>;
    resume: (id: number) => Promise<MktCampaign | MktError>;
    report: (id: number) => Promise<MktCampaignReport | MktError>;
    sends: (id: number, status?: string, page?: number) => Promise<MktPage<MktSend & { name: string | null }> | MktError>;
    // AI connection: keys for outside tools (managers) and what those tools hand in for review
    apiKeys: () => Promise<MktApiKey[] | MktError>;
    createApiKey: (input: MktApiKeyInput) => Promise<MktApiKeyCreated | MktError>;
    revokeApiKey: (id: number) => Promise<MktApiKey | MktError>;
    proposals: (status?: MktProposalStatus) => Promise<MktProposalView[] | MktError>;
    proposal: (id: number) => Promise<MktProposalView | MktError>;
    approveProposal: (id: number) => Promise<MktProposalView | MktError>;
    rejectProposal: (id: number, note: string) => Promise<MktProposalView | MktError>;
    // Signals: custom events people and tools record, plus the built-in ones
    signals: () => Promise<MktSignalsView | MktError>;
    createSignal: (input: MktSignalInput & { key: string; label: string }) => Promise<MktSignal | MktError>;
    updateSignal: (id: number, input: MktSignalInput) => Promise<MktSignal | MktError>;
    archiveSignal: (id: number) => Promise<MktSignal | MktError>;
    signalDaily: (key: string, days?: number) => Promise<MktSignalDay[] | MktError>;
    // OAuth consent: a signed-in manager lets an app (claude.ai, ChatGPT) connect
    oauthRequest: (request: string) => Promise<MktOAuthRequestView | MktError>;
    oauthApprove: (input: { request: string; scopes: string[]; expires_in_days?: number }) => Promise<{ redirect: string } | MktError>;
    oauthDeny: (request: string) => Promise<{ redirect: string } | MktError>;

    // Flows
    flows: () => Promise<MktFlowListItem[] | MktError>;
    flowStarters: () => Promise<MktFlowStarter[] | MktError>;
    createFlow: (input: { name: string; description?: string; starter?: string }) => Promise<MktFlow | MktError>;
    flow: (id: number) => Promise<MktFlowDetail | MktError>;
    updateFlow: (id: number, input: MktFlowInput) => Promise<MktFlow | MktError>;
    checkFlow: (id: number) => Promise<MktFlowCheck | MktError>;
    publishFlow: (id: number) => Promise<MktFlow | MktError>;
    pauseFlow: (id: number, reason?: string) => Promise<MktFlow | MktError>;
    resumeFlow: (id: number) => Promise<MktFlow | MktError>;
    archiveFlow: (id: number) => Promise<MktFlow | MktError>;
    duplicateFlow: (id: number) => Promise<MktFlow | MktError>;
    discardFlowDraft: (id: number) => Promise<MktFlow | MktError>;
    flowReport: (id: number) => Promise<MktFlowReport | MktError>;
    flowPeople: (id: number, status?: MktFlowPeopleStatus, page?: number) => Promise<MktPage<MktFlowPerson> | MktError>;
    flowPerson: (id: number, enrollmentId: number) => Promise<{ person: MktFlowPerson; log: MktFlowLog[] } | MktError>;

    links: () => Promise<MktLinksView | MktError>;
    saveLink: (input: MktLinkInput) => Promise<MktTrackedLink | MktError>;
    archiveLink: (id: number) => Promise<true | MktError>;
    attribution: (query: { from?: string; to?: string; model?: MktAttributionModel; group?: MktAttributionGroup }) => Promise<MktAttributionReport | MktError>;

    analyticsOverview: (query: MktAnalyticsQuery) => Promise<MktAnalyticsOverview | MktError>;
    flowAnalytics: (id: number, query: MktAnalyticsQuery) => Promise<MktFlowAnalytics | MktError>;
    flowSummaries: (query: MktAnalyticsQuery) => Promise<{ range: MktAnalyticsRange; flows: MktFlowSummary[] } | MktError>;
};

// #region useMarketing
/**
 * Marketing (email campaigns). Server routes are under /v2/marketing; the workspace is
 * Wallmates unless `workspaceId` is given (brand workspaces are the future add-on).
 */
export const useMarketing = (workspaceId?: number): [MarketingKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();

    const fns = useMemo<MarketingKeyFunctions>(() => {
        const headers: Record<string, string> = { 'X-Wallmates-Auth': authToken ?? '' };
        if (workspaceId) headers['X-Mkt-Workspace'] = String(workspaceId);

        const handle = (err: any): MktError => {
            const status = err?.response?.status ?? 0;
            if (status === 401) loginFunctions.logout();
            return {
                ok: false,
                status,
                message: err?.response?.data?.message
                    || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
            };
        };
        const call = async <T>(method: 'get' | 'post' | 'put' | 'delete', path: string, body?: unknown, params?: Record<string, unknown>): Promise<T | MktError> => {
            try {
                const r: AxiosResponse<T> = await axios.request({ method, url: `${baseUrl}${path}`, data: body, params, headers });
                return r.data;
            } catch (err: any) { return handle(err); }
        };
        const ok = async (p: Promise<unknown>): Promise<true | MktError> => {
            const r = await p;
            return isMktError(r) ? r : true;
        };

        return {
            workspace: () => call('get', '/workspace'),
            saveWorkspace: (input) => call('put', '/workspace', input),
            overview: () => call('get', '/overview'),
            catalog: () => call('get', '/catalog'),
            saveTopic: (topic) => topic.id ? call('put', `/topics/${topic.id}`, topic) : call('post', '/topics', topic),
            audit: () => call('get', '/audit'),

            contacts: (query) => call('get', '/contacts', undefined, query as Record<string, unknown>),
            contact: (id) => call('get', `/contacts/${id}`),
            setConsent: (id, topicId, status, note) => call('put', `/contacts/${id}/consent`, { topic_id: topicId, status, note }),
            suppress: (id, note) => call('post', `/contacts/${id}/suppress`, { note }),
            unsuppress: (id, note) => call('delete', `/contacts/${id}/suppress`, { note }),
            erase: (id) => call('post', `/contacts/${id}/erase`),
            exportContact: (id) => call('get', `/contacts/${id}/export`),
            importContacts: (rows, topicKeys, basis, wording) => call('post', '/contacts/import', { rows, topic_keys: topicKeys, basis, wording }),
            syncContacts: () => call('post', '/contacts/sync'),

            segments: () => call('get', '/segments'),
            segment: (id) => call('get', `/segments/${id}`),
            createSegment: (input) => call('post', '/segments', input),
            updateSegment: (id, input) => call('put', `/segments/${id}`, input),
            archiveSegment: (id) => ok(call('delete', `/segments/${id}`)),
            previewSegment: (definition) => call('post', '/segments/preview', { definition }),
            whyInSegment: (id, contactId) => call('get', `/segments/${id}/why/${contactId}`),

            templates: () => call('get', '/templates'),
            template: (id) => call('get', `/templates/${id}`),
            createTemplate: (input) => call('post', '/templates', input),
            updateTemplate: (id, input) => call('put', `/templates/${id}`, input),
            duplicateTemplate: (id) => call('post', `/templates/${id}/duplicate`),
            archiveTemplate: (id) => ok(call('delete', `/templates/${id}`)),
            render: (input) => call('post', '/templates/render', input),
            sendTest: (input) => call('post', '/templates/test', input),

            campaigns: () => call('get', '/campaigns'),
            campaign: (id) => call('get', `/campaigns/${id}`),
            createCampaign: (input) => call('post', '/campaigns', input),
            updateCampaign: (id, input) => call('put', `/campaigns/${id}`, input),
            duplicateCampaign: (id) => call('post', `/campaigns/${id}/duplicate`),
            preflight: (id) => call('get', `/campaigns/${id}/preflight`),
            submit: (id, note) => call('post', `/campaigns/${id}/submit`, { note }),
            approve: (id) => call('post', `/campaigns/${id}/approve`),
            reject: (id, note) => call('post', `/campaigns/${id}/reject`, { note }),
            cancel: (id) => call('post', `/campaigns/${id}/cancel`),
            pause: (id) => call('post', `/campaigns/${id}/pause`),
            resume: (id) => call('post', `/campaigns/${id}/resume`),
            report: (id) => call('get', `/campaigns/${id}/report`),
            sends: (id, status, page) => call('get', `/campaigns/${id}/sends`, undefined, { status, page }),

            apiKeys: () => call('get', '/api-keys'),
            createApiKey: (input) => call('post', '/api-keys', input),
            revokeApiKey: (id) => call('delete', `/api-keys/${id}`),
            proposals: (status) => call('get', '/proposals', undefined, status ? { status } : undefined),
            proposal: (id) => call('get', `/proposals/${id}`),
            approveProposal: (id) => call('post', `/proposals/${id}/approve`),
            rejectProposal: (id, note) => call('post', `/proposals/${id}/reject`, { note }),
            signals: () => call('get', '/signals'),
            createSignal: (input) => call('post', '/signals', input),
            updateSignal: (id, input) => call('put', `/signals/${id}`, input),
            archiveSignal: (id) => call('post', `/signals/${id}/archive`),
            signalDaily: (key, days = 30) => call('get', `/signals/${encodeURIComponent(key)}/daily`, undefined, { days }),
            oauthRequest: (request) => call('get', '/oauth/request', undefined, { request }),
            oauthApprove: (input) => call('post', '/oauth/approve', input),
            oauthDeny: (request) => call('post', '/oauth/deny', { request }),

            flows: () => call('get', '/flows'),
            flowStarters: () => call('get', '/flows/starters'),
            createFlow: (input) => call('post', '/flows', input),
            flow: (id) => call('get', `/flows/${id}`),
            updateFlow: (id, input) => call('put', `/flows/${id}`, input),
            checkFlow: (id) => call('post', `/flows/${id}/check`),
            publishFlow: (id) => call('post', `/flows/${id}/publish`),
            pauseFlow: (id, reason) => call('post', `/flows/${id}/pause`, { reason }),
            resumeFlow: (id) => call('post', `/flows/${id}/resume`),
            archiveFlow: (id) => call('post', `/flows/${id}/archive`),
            duplicateFlow: (id) => call('post', `/flows/${id}/duplicate`),
            discardFlowDraft: (id) => call('post', `/flows/${id}/discard`),
            flowReport: (id) => call('get', `/flows/${id}/report`),
            flowPeople: (id, status, page) => call('get', `/flows/${id}/people`, undefined, { status, page }),
            flowPerson: (id, enrollmentId) => call('get', `/flows/${id}/people/${enrollmentId}`),

            links: () => call('get', '/links'),
            saveLink: (input) => call('post', '/links', input),
            archiveLink: (id) => ok(call('delete', `/links/${id}`)),
            attribution: (query) => call('get', '/attribution', undefined, query),

            analyticsOverview: (query) => call('get', '/analytics/overview', undefined, { ...query }),
            flowAnalytics: (id, query) => call('get', `/flows/${id}/analytics`, undefined, { ...query }),
            flowSummaries: (query) => call('get', '/flows/analytics/summary', undefined, { ...query })
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, workspaceId]);

    return [fns];
};

// #region public (no login)
const publicCall = async <T>(method: 'get' | 'post', path: string, body?: unknown): Promise<T | MktError> => {
    try {
        const r: AxiosResponse<T> = await axios.request({ method, url: `${publicUrl}${path}`, data: body });
        return r.data;
    } catch (err: any) {
        const status = err?.response?.status ?? 0;
        return { ok: false, status, message: err?.response?.data?.message || 'This link has expired or is not valid.' };
    }
};

/** The preference page behind every marketing email's "Manage preferences" link. */
export const marketingPublic = {
    preferences: (token: string) => publicCall<MktPreferencePage>('get', `/p/${encodeURIComponent(token)}`),
    savePreferences: (token: string, input: { topics?: Record<string, boolean>; unsubscribe_all?: boolean; resubscribe?: boolean }) =>
        publicCall<MktPreferencePage>('post', `/p/${encodeURIComponent(token)}`, input),
    confirm: (token: string) => publicCall<MktPreferencePage>('post', `/confirm/${encodeURIComponent(token)}`)
};
